import { AddColumnBodyCell } from "@/features/data-curation/components/AddColumnCells";
import { CorpusCountText } from "@/features/data-curation/components/CorpusCountText";
import { NUMBER_CELL, ROW_CELL, VALUE_CELL } from "@/features/data-curation/gridCells";
import {
  formatCount,
  hasCorpusCount,
  ROW_LIMIT_CONTACT,
} from "@/features/data-curation/rowLimit";
import type {
  CurationColumn,
  ExtractionCorpusEstimate,
} from "@/features/data-curation/types";
import { Lock } from "lucide-react";

const ROW_OPACITY = ["opacity-80", "opacity-60", "opacity-40"];
const BAR_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-5/6", "w-2/5"];

const Bar = ({ className }: { className: string }) => (
  <span className={`block h-2 rounded-full bg-zinc-300 blur-[2px] ${className}`} />
);

interface LockedGridRowsProps {
  columns: CurationColumn[];
  firstRowNumber: number;
  rowLimit: number;
  estimate?: ExtractionCorpusEstimate | null;
  /** The grid ends with the Add column button. */
  addColumnCell?: boolean;
}

/** Blurred rows under the last row of a curation that can take no more rows. */
export const LockedGridRows = ({
  columns,
  firstRowNumber,
  rowLimit,
  estimate,
  addColumnCell = false,
}: LockedGridRowsProps) => {
  return (
    <>
      {ROW_OPACITY.map((opacity, index) => (
        <tr key={opacity} aria-hidden data-locked-row className="select-none">
          <td
            className={`${NUMBER_CELL} z-10 bg-white px-1 py-1.5 text-right align-top text-2xs tabular-nums text-zinc-400`}
          >
            <span className={opacity}>{firstRowNumber + index}</span>
          </td>
          <td className={`${ROW_CELL} z-10 bg-white px-2 py-2.5 align-top`}>
            <span className={`block ${opacity}`}>
              <Bar className="w-24" />
              <Bar className="mt-2 w-10" />
            </span>
          </td>
          {columns.map((column, columnIndex) => (
            <td
              key={column.column_id}
              className={`${VALUE_CELL} border-zinc-200 px-2 py-2.5 align-top`}
            >
              <span className={`block ${opacity}`}>
                <Bar className={BAR_WIDTHS[(index + columnIndex) % BAR_WIDTHS.length]} />
                <Bar
                  className={`mt-2 ${BAR_WIDTHS[(index + columnIndex + 2) % BAR_WIDTHS.length]}`}
                />
              </span>
            </td>
          ))}
          {addColumnCell && <AddColumnBodyCell />}
        </tr>
      ))}
      <tr>
        <td colSpan={columns.length + 2} className="border-b border-zinc-200 bg-white p-0">
          <div
            className={`sticky left-0 flex w-full items-start gap-2 px-3 py-3 text-xs leading-5 text-zinc-600 ${
              addColumnCell ? "max-w-[min(40rem,100cqw_-_2.25rem)]" : "max-w-[min(40rem,100cqw)]"
            }`}
          >
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
            <p>
              This dataset has reached its limit of {formatCount(rowLimit)} rows.
              {hasCorpusCount(estimate) && (
                <>
                  {" "}
                  <CorpusCountText estimate={estimate} />
                </>
              )}
              {" "}For more rows, contact{" "}
              <a
                href={`mailto:${ROW_LIMIT_CONTACT}`}
                className="font-medium text-zinc-800 underline underline-offset-2"
              >
                {ROW_LIMIT_CONTACT}
              </a>
              .
            </p>
          </div>
        </td>
        {addColumnCell && <AddColumnBodyCell />}
      </tr>
    </>
  );
};
